import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ButtonLoadingContent, loadingButtonClass } from "@/components/buttons/ButtonLoadingContent";
import { BrandDetailsSkeleton } from "@/components/skeletons";
import { deleteBrand, getBrandById } from "@/services/setting.service";
import { invalidateListCacheByPage } from "@/utils/listPageCache";
import { BUTTONS, HEADINGS, LABELS, TEXT } from "@/utils/constants/appConstants/commonConstants";
import { cn } from "@/lib/utils";

type BrandFormItemProps = {
    id: string;
    label: string;
    endAlign?: boolean;
    alignStart?: boolean;
    valueClassName?: string;
    children: React.ReactNode;
};

function BrandFormItem({
    id,
    label,
    endAlign = false,
    alignStart = false,
    valueClassName,
    children,
}: BrandFormItemProps) {
    return (
        <div className="gs-brand-form-item">
            <div
                className={cn(
                    "gs-brand-form-col",
                    endAlign && "gs-brand-form-col--end",
                    alignStart && "gs-brand-form-col--align-start"
                )}
            >
                <div id={`${id}-label`} className="gs-brand-form-label">
                    {label}
                </div>
                <div
                    id={`${id}-value`}
                    className={cn(
                        "gs-brand-form-value",
                        endAlign && "flex-1",
                        valueClassName
                    )}
                >
                    {children}
                </div>
            </div>
        </div>
    );
}

function getBrandLogoSrc(logo?: string) {
    if (!logo) return "";
    const parts = logo.split(",");
    if (parts.length < 2) return logo;
    return `${parts[0]},${parts[1]}`;
}

const BrandDetails = () => {
    const [brandDetails, setBrandDetails] = useState<any>({});
    const [isBrandDetailsLoading, setIsBrandDetailsLoading] = useState(true);
    const [isDeleteBrandLoading, setIsDeleteBrandLoading] = useState(false);
    const params = useParams();
    const navigate = useNavigate();
    const id = params.id || "";

    useEffect(() => {
        setIsBrandDetailsLoading(true);
        getBrandById(
            id,
            (response: any) => {
                setIsBrandDetailsLoading(false);
                setBrandDetails(response);
            },
            (error: any) => {
                setIsBrandDetailsLoading(false);
                console.log(error);
            }
        );
    }, [id]);

    const handleDeleteBrand = () => {
        setIsDeleteBrandLoading(true);
        deleteBrand(
            id,
            () => {
                setIsDeleteBrandLoading(false);
                invalidateListCacheByPage("brands");
                navigate("/brands");
            },
            (error: any) => {
                setIsDeleteBrandLoading(false);
                console.log(error);
            }
        );
    };

    if (isBrandDetailsLoading) {
        return <BrandDetailsSkeleton />;
    }

    return (
        <Card className="gs-brand-form-card gs-list-page-card gap-0 py-0 shadow-none">
            <div className="gs-brand-form-inner">
                <h4 className="gs-brand-form-title" id="brand-detail-heading">
                    {HEADINGS.BRANDING}
                </h4>
                <p className="gs-brand-detail-id" id="brand-detail-id">
                    {LABELS.BRAND_ID}: {brandDetails.id}
                </p>

                <div className="gs-brand-form-head">
                    <h6 className="gs-brand-form-head-title" id="brand-detail-info">
                        {HEADINGS.INFO}
                    </h6>
                </div>

                <div className="gs-brand-form-group">
                    <BrandFormItem id="brand-detail-name" label={LABELS.BRAND_NAME}>
                        <Input
                            type="text"
                            className="gs-brand-form-input max-w-[250px]"
                            id="brand-name"
                            value={brandDetails.name ?? ""}
                            disabled
                            readOnly
                        />
                    </BrandFormItem>

                    <BrandFormItem id="brand-detail-company-name" label={LABELS.COMPANY_NAME}>
                        <Input
                            type="text"
                            className="gs-brand-form-input max-w-[250px]"
                            id="company-name"
                            value={brandDetails.company_name ?? ""}
                            disabled
                            readOnly
                        />
                    </BrandFormItem>
                </div>

                <div className="gs-brand-form-section">
                    <div className="gs-brand-form-head">
                        <h6 className="gs-brand-form-head-title" id="brand-detail-customize-recipient">
                            {HEADINGS.CUSTOMIZE_WHAT_RECIPIENT_SEE}
                        </h6>
                    </div>

                    <BrandFormItem
                        id="brand-detail-add-brand-logo-color"
                        label={TEXT.ADD_LOGO_AND_BRAND}
                        endAlign
                        alignStart
                        valueClassName="gs-brand-form-value--brand-preview"
                    >
                        <div className="gs-brand-detail-customize-value">
                            <div className="gs-brand-detail-swatches">
                                <span
                                    id="brand-detail-header-bcackground-color"
                                    className="gs-brand-detail-swatch"
                                    style={{ backgroundColor: brandDetails?.colors?.headers?.background }}
                                    aria-hidden
                                />
                                <span
                                    id="brand-detail-header-text-color"
                                    className="gs-brand-detail-swatch"
                                    style={{ backgroundColor: brandDetails?.colors?.headers?.text }}
                                    aria-hidden
                                />
                                <span
                                    id="brand-detail-button-background-color"
                                    className="gs-brand-detail-swatch"
                                    style={{ backgroundColor: brandDetails?.colors?.buttons?.background }}
                                    aria-hidden
                                />
                                <span
                                    id="brand-detail-button-text-color"
                                    className="gs-brand-detail-swatch"
                                    style={{ backgroundColor: brandDetails?.colors?.buttons?.text }}
                                    aria-hidden
                                />
                            </div>
                            <div className="gs-brand-detail-preview">
                                {brandDetails?.logos?.email ? (
                                    <img
                                        id="brand-detail-email-logo"
                                        className="gs-brand-detail-preview__email"
                                        src={getBrandLogoSrc(brandDetails.logos.email)}
                                        alt="Email branding preview"
                                    />
                                ) : null}
                                {brandDetails?.logos?.signing ? (
                                    <img
                                        id="brand-detail-signing-logo"
                                        className="gs-brand-detail-preview__signing"
                                        src={getBrandLogoSrc(brandDetails.logos.signing)}
                                        alt="Signing branding preview"
                                    />
                                ) : null}
                            </div>
                        </div>
                    </BrandFormItem>
                </div>

                <div className="mt-6 border-t-[1px] pt-4 border-border delete-button">
                    <Button
                        id="brand-detail-delete-btn"
                        type="button"
                        variant="light"
                        className={loadingButtonClass(isDeleteBrandLoading, "uppercase")}
                        disabled={isDeleteBrandLoading}
                        aria-busy={isDeleteBrandLoading || undefined}
                        onClick={handleDeleteBrand}
                    >
                        <ButtonLoadingContent
                            loading={isDeleteBrandLoading}
                            loaderId="brand-detail-delete-loader"
                            spinnerClassName="text-secondary"
                        >
                            {BUTTONS.DELETE}
                        </ButtonLoadingContent>
                    </Button>
                </div>
            </div>
        </Card>
    );
};

export default BrandDetails;
